import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Heart, Users, Sparkles } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const AboutSection = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: Users,
      title: t('about.feature1Title'),
      description: t('about.feature1Desc'),
    },
    {
      icon: Heart,
      title: t('about.feature2Title'),
      description: t('about.feature2Desc'),
    },
    {
      icon: Sparkles,
      title: t('about.feature3Title'),
      description: t('about.feature3Desc'),
    },
  ];

  return (
    <section 
      id="about"
      className="section-padding bg-cream relative overflow-hidden"
      aria-labelledby="about-title"
    >
      {/* Decorative element */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-sage/5 rounded-full blur-3xl"></div>
      
      <div className="container-main relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <span 
            className="inline-block font-body text-sm uppercase tracking-widest font-semibold text-sage mb-4"
            data-testid="about-caption"
          >
            {t('about.caption')}
          </span>
          <h2 
            id="about-title"
            className="font-heading text-4xl md:text-5xl font-semibold text-[#1C1C1E] tracking-tight mb-6"
            data-testid="about-title"
          >
            {t('about.title')}
          </h2>
          <p 
            className="font-body text-lg text-muted-foreground leading-relaxed"
            data-testid="about-description"
          >
            {t('about.description')}
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Placeholder */}
          <div className="relative max-w-md mx-auto lg:mx-0">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-earth/50 aspect-square flex items-center justify-center">
              <img 
                src="/images/1234567890.jpg"
                alt="Dr. Choi - Master of Acupuncture at Healing Clinic"
                className="w-full h-full object-cover"
                data-testid="about-doctor-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 bg-earth/30" style={{display: 'flex'}}>
              </div>
            </div>
            {/* Decorative accent */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gold/20 rounded-2xl -z-10"></div>
            <div className="absolute -top-4 -left-4 w-20 h-20 bg-sage/10 rounded-2xl -z-10"></div>
          </div>

          {/* Features */}
          <div className="space-y-10">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className="service-card border-earth-dark hover:-translate-y-1 transition-transform duration-300"
                data-testid={`about-feature-${index}`}
              >
                <CardContent className="p-6 flex items-start gap-5">
                  <div className="flex-shrink-0 w-12 h-12 bg-sage/10 rounded-xl flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-sage" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-[#1C1C1E] mb-2">
                      {feature.title}
                    </h3>
                    <p className="font-body text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
